import { FC } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

interface SkeletonBlockProps {
  variant?: "markdown" | "keyValue" | "taskList" | "table";
  delay?: number;
}

export const SkeletonBlock: FC<SkeletonBlockProps> = ({ variant = "markdown", delay = 0 }) => {
  return (
    <Card style={{ animationDelay: `${delay}ms` }}>
      <Header>
        <Dot />
        <BarTitle />
      </Header>
      <Body>{renderBody(variant)}</Body>
    </Card>
  );
};

const renderBody = (variant: SkeletonBlockProps["variant"]) => {
  switch (variant) {
    case "keyValue":
      return (
        <Grid>
          {Array.from({ length: 4 }).map((_, i) => (
            <KvRow key={i}>
              <KvLabel />
              <KvValue />
            </KvRow>
          ))}
        </Grid>
      );
    case "taskList":
      return (
        <Lines>
          {Array.from({ length: 3 }).map((_, i) => (
            <TaskRow key={i}>
              <Checkbox />
              <Bar style={{ width: `${60 + i * 8}%` }} />
            </TaskRow>
          ))}
        </Lines>
      );
    case "table":
      return (
        <Table>
          {Array.from({ length: 3 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          ))}
        </Table>
      );
    case "markdown":
    default:
      return (
        <Lines>
          <Bar style={{ width: "92%" }} />
          <Bar style={{ width: "85%" }} />
          <Bar style={{ width: "70%" }} />
        </Lines>
      );
  }
};

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing["4"]};
  background: ${({ theme }) => theme.colors.surface.pane};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  opacity: 0;
  animation: ${fadeIn} 280ms ease-out forwards;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const shimmerStyles = ({ theme }: { theme: { colors: { border: { default: string } } } }) => `
  background: linear-gradient(
    90deg,
    ${theme.colors.border.default}55 0%,
    ${theme.colors.border.default}aa 50%,
    ${theme.colors.border.default}55 100%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s linear infinite;
  border-radius: 4px;
`;

const Dot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  ${shimmerStyles}
`;

const BarTitle = styled.div`
  height: 14px;
  width: 40%;
  ${shimmerStyles}
`;

const Bar = styled.div`
  height: 12px;
  ${shimmerStyles}
`;

const Body = styled.div``;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
`;

const KvRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const KvLabel = styled.div`
  height: 9px;
  width: 60%;
  ${shimmerStyles}
`;

const KvValue = styled.div`
  height: 13px;
  width: 80%;
  ${shimmerStyles}
`;

const TaskRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.div`
  width: 14px;
  height: 14px;
  ${shimmerStyles}
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
`;

const TableCell = styled.div`
  height: 12px;
  ${shimmerStyles}
`;
